import type { Decor } from '../world/mapdef';
import { hasGame, S } from '../core/game';
import { block, ellipse, rect, shadow, sun, type Ctx } from './paint';
import { wind } from './nature';
import { registerPropDrawers } from './propRegistry';
import { drawCharacter, type CharLook } from './sprites';
import { LIFE_ANIMATED, drawLife } from './streetLife';

/**
 * Extra life on Liberty Square (cks map), kept apart from propsCks.ts (which also holds Beitou):
 * the shared street-life kinds under the `ck_` prefix (`ck_crowd`, `ck_benchSit`, `ck_parterre`, ...),
 * the relief guards marching along Liberty Boulevard on the hour, and the national flagpole.
 */

const isNight = () => sun.alpha < 0.8;
const now = () => (hasGame() ? S().time : 12 * 60);

const GUARD: CharLook = { skin: '#e8b48c', hair: '#1c1814', hairStyle: 'cap', hat: '#f4f5f2', top: '#f4f5f2', topShade: '#d0d4d8', bottom: '#f4f5f2', shoes: '#1d1814', badge: '#e8c048', tie: '#1f3a6e' };

function polyFill(c: Ctx, col: string, pts: number[]): void {
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.closePath();
  c.fill();
}

function line(c: Ctx, col: string, w: number, pts: number[]): void {
  c.strokeStyle = col;
  c.lineWidth = w;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.stroke();
}

/**
 * The changing of the guard: in the first ten minutes of every hour (9:00–17:00) a file of three
 * guards marches from the gate towards the hall along the boulevard; in the last ten they march back.
 * `w` = length of the march (px), anchor = west end.
 */
function guardWalk(c: Ctx, d: Decor, t: number): void {
  const tm = now();
  const h = Math.floor(tm / 60), mi = tm % 60;
  if (h < 9 || h > 17) return;
  const east = mi < 10, west = mi >= 50;
  if (!east && !west) return;
  const u = east ? mi / 10 : (mi - 50) / 10;
  const w = d.w ?? 600;
  const x0 = d.x + (east ? u : 1 - u) * w;
  for (let i = 0; i < 3; i++) {
    const gx = x0 - i * 16, gy = d.y + (i % 2) * 3;
    drawCharacter(c, GUARD, gx, gy, { facing: east ? 'right' : 'left', phase: (t * 1.6 + i * 0.5) % 1, moving: true, blink: false, breath: 0 });
    // Rifle shouldered, bayonet up.
    const hx = gx + (east ? 4 : -4), hy = gy - 14;
    line(c, '#5a3a20', 1.4, [hx, hy + 3, hx + (east ? 1 : -1) * 2, hy - 8]);
    line(c, '#c8cdd2', 0.8, [hx + (east ? 1 : -1) * 2, hy - 8, hx + (east ? 1 : -1) * 2.6, hy - 12]);
  }
}

/** A tall white flagpole with the national flag waving in the wind. */
function flagpole(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, h = d.h ?? 84;
  shadow(c, x, y, 7, 2.2, 0.3, 40);
  block(c, x - 6, y - 5, 12, 5, '#d6d2c8', '#f4f2ec', '#a8a49c');
  block(c, x - 4, y - 8, 8, 3, '#e8e4dc', '#ffffff', '#b8b4ac');
  rect(c, '#f4f2ec', x - 1, y - h, 2, h - 8);
  rect(c, 'rgba(0,0,0,0.15)', x + 0.5, y - h, 0.7, h - 8);
  ellipse(c, '#e8c048', x, y - h - 1.5, 1.8, 1.8);
  // The flag: red field, blue canton with the white sun, rippling.
  const fw = 26, fh = 17, top = y - h + 2;
  const g = wind.at(x, y) * 3 + 2;
  c.save();
  c.beginPath();
  c.moveTo(x + 1, top);
  for (let i = 0; i <= 6; i++) c.lineTo(x + 1 + (i / 6) * fw, top + Math.sin(t * 4 + i * 0.9) * g * (i / 6));
  for (let i = 6; i >= 0; i--) c.lineTo(x + 1 + (i / 6) * fw, top + fh + Math.sin(t * 4 + i * 0.9) * g * (i / 6));
  c.closePath();
  c.clip();
  rect(c, '#d7281f', x + 1, top - 4, fw, fh + 8);
  rect(c, '#1d3f9a', x + 1, top - 4, fw / 2, fh / 2 + 4);
  const sx = x + 1 + fw / 4, sy = top + fh / 4 - 0.4;
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    polyFill(c, '#ffffff', [sx + Math.cos(a) * 3.6, sy + Math.sin(a) * 3.6, sx + Math.cos(a + 0.26) * 1.8, sy + Math.sin(a + 0.26) * 1.8, sx + Math.cos(a - 0.26) * 1.8, sy + Math.sin(a - 0.26) * 1.8]);
  }
  ellipse(c, '#1d3f9a', sx, sy, 2, 2);
  ellipse(c, '#ffffff', sx, sy, 1.5, 1.5);
  for (let i = 0; i <= 6; i++) {
    const k = i / 6;
    rect(c, `rgba(0,0,0,${0.08 + Math.max(0, Math.sin(t * 4 + i * 0.9)) * 0.1})`, x + 1 + k * fw, top - 4, fw / 6 + 0.5, fh + 8);
  }
  c.restore();
  if (isNight()) ellipse(c, 'rgba(255,240,200,0.18)', x + 12, top + fh / 2, 18, 12);
}

const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';
function txt(c: Ctx, s: string, x: number, y: number, size: number, col: string, weight = 900): void {
  c.font = `${weight} ${size}px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = col;
  c.fillText(s, x, y);
}

const POSTERS: [string, string, string, string, string][] = [
  ['雲門舞集', 'CLOUD GATE', '#1b1d24', '#ffd34e', '#e2493b'],
  ['歌劇魅影', 'THE PHANTOM', '#2a1a3a', '#f4f1ea', '#c58cff'],
  ['交響之夜', 'SYMPHONY NIGHT', '#1f3a6e', '#ffffff', '#ffd34e'],
  ['京劇', 'PEKING OPERA', '#c0392b', '#fff3b0', '#1b1d24'],
];

/** A show poster board on two posts outside the theatres (`variant` picks the poster), lit at night. */
function poster(c: Ctx, d: Decor, t: number): void {
  const v = (d.variant ?? 0) % POSTERS.length;
  const [zh, en, bg, ink, accent] = POSTERS[v];
  const x = d.x, y = d.y;
  shadow(c, x, y, 13, 2.2);
  for (const px of [x - 10, x + 8.5]) block(c, px, y - 18, 1.8, 18, '#55606a', '#7a8690');
  block(c, x - 13, y - 40, 26, 23, '#2a2e33', '#4a4e54', '#1b1d24');
  rect(c, bg, x - 11.5, y - 38.5, 23, 20);
  // A splash of colour: a dancer's arc / a mask shape.
  ellipse(c, accent, x + 4, y - 31, 5, 7);
  ellipse(c, bg, x + 6, y - 30, 3.2, 5);
  ellipse(c, accent, x - 6, y - 25, 3, 1.6);
  txt(c, zh, x - 1, y - 33.5, 5, ink);
  txt(c, en, x, y - 22.5, 2.2, ink, 700);
  rect(c, accent, x - 10, y - 20.6, 20, 0.6);
  if (isNight()) {
    ellipse(c, 'rgba(255,240,200,0.22)', x, y - 29, 14, 11);
    ellipse(c, '#fff3b0', x, y - 41.5, 1.2, 1.2);
  }
  void t;
}

function drawDecor(c: Ctx, d: Decor, t: number): boolean {
  if (!d.kind.startsWith('ck_')) return false;
  switch (d.kind) {
    case 'ck_guardWalk':
      guardWalk(c, d, t);
      return true;
    case 'ck_flagpole':
      flagpole(c, d, t);
      return true;
    case 'ck_poster':
      poster(c, d, t);
      return true;
    default:
      return drawLife(c, d.kind.slice(3), d, t);
  }
}

registerPropDrawers({
  decor: drawDecor,
  animatedDecor: ['ck_guardWalk', 'ck_flagpole', ...LIFE_ANIMATED.map((k) => `ck_${k}`)],
});
